import type { Editor, EditorEvent } from 'hugerte';

import type { TranslationKey } from '@change-corgi/core/i18n';

import { MAX_IMAGE_FILE_SIZE, UPLOAD_ERROR_MESSAGES } from 'src/app/shared/hooks/uppyUploader';
import { getImageCount } from 'src/app/shared/utils/richMedia';

import { MAX_IMAGE_COUNT, MAX_IMAGE_ERROR_TEXT } from '../constants';

import { acceptedRichMediaImageTypes } from './imagesUploadHandler';

type OnDropValidator = ({ editor, event }: { editor: Editor; event: EditorEvent<DragEvent> }) => TranslationKey | null;

const validateImageCount: OnDropValidator = ({ editor }) => {
	const imageCount = getImageCount(editor.getContent());
	if (imageCount >= MAX_IMAGE_COUNT) return MAX_IMAGE_ERROR_TEXT;
	return null;
};

const validateFileType: OnDropValidator = ({ event }) => {
	// TODO fix typings
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const files = [...((event.dataTransfer?.files || []) as any)] as File[];
	return files.reduce<TranslationKey | null>((errorKey, file) => {
		if (!errorKey && !acceptedRichMediaImageTypes.includes(file.type)) {
			return UPLOAD_ERROR_MESSAGES.UNSUPPORTED_FILE_TYPE;
		}
		return errorKey;
	}, null);
};

const validateFileSize: OnDropValidator = ({ event }) => {
	// TODO fix typings
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const files = [...((event.dataTransfer?.files || []) as any)] as File[];
	return files.reduce<TranslationKey | null>((errorKey, file) => {
		if (!errorKey && file.size > MAX_IMAGE_FILE_SIZE) {
			return UPLOAD_ERROR_MESSAGES.IMAGE_TOO_LARGE;
		}
		return errorKey;
	}, null);
};

const validateSingleFile: OnDropValidator = ({ event }) => {
	// TODO fix typings
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	const files = [...((event.dataTransfer?.files || []) as any)] as File[];
	if (files.length > 1) return UPLOAD_ERROR_MESSAGES.DROP_ONE_AT_A_TIME;
	return null;
};

const onDropValidators: OnDropValidator[] = [
	validateImageCount,
	validateSingleFile,
	validateFileType,
	validateFileSize,
];

export const onDropCallback =
	({ editor, setErrorKey }: { editor: Editor; setErrorKey: (errorKey: TranslationKey | null) => void }) =>
	(event: EditorEvent<DragEvent>): void => {
		setErrorKey(null);
		if (!event.dataTransfer?.files) {
			event.stopPropagation();
			event.preventDefault();
		} else {
			const errorKey = onDropValidators.reduce<TranslationKey | null>(
				(error, validator) => error ?? validator({ event, editor }),
				null,
			);

			if (errorKey) {
				setErrorKey(errorKey);
				event.stopPropagation();
				event.preventDefault();
			}
		}
	};
